// The two glyphs SkyNavDrawer itself uses for its collapse toggle — see the
// button below, which stands in for that toggle on small screens.
import { Menu, PanelLeftClose } from "lucide-react";
import { useState, useEffect } from "react";
import { SkyLink, SkyButton } from "@skypointcloud/skypoint-ui";
import { ROUTES } from "../../../components/shared/constant";

import { useMsalAuth } from "../../../hooks/useMsalAuth";
import { ModeToggle } from "../../../components/packs/ModeToggle";
import { GlobalCommunitySelector } from "./GlobalCommunitySelector";
import { NotificationBell } from "../../../components/Notifications/NotificationBell";
import { RecentlyViewedMenu } from "../../../components/RecentlyViewed/RecentlyViewedMenu";
import { NewVersionBanner } from "../../../components/shared/NewVersionBanner";
import SidebarSky, { MOBILE_DRAWER_ID } from "../sidebar/sidebar";
import { TenantLogo } from "./TenantLogo";

// Import extracted hooks
import { useHeaderData } from "./hooks";

// Import extracted components
import { ProfileMenu } from "./components";

/**
 * `window.matchMedia(query).matches`, correct on the FIRST render.
 *
 * The design system's `useMediaQuery` is `useState(false)` plus a `useEffect`
 * that reads matchMedia afterwards, which is fine for a query whose false state
 * is also its resting state, and wrong for this one: here `false` means "narrow",
 * so every desktop load paints a mobile header before correcting itself. Seeding
 * from a useState initialiser removes that frame; the effect still subscribes, so
 * resizes and zoom changes behave exactly as before.
 */
function useMatchesQuery(query: string): boolean {
  const read = () =>
    typeof window !== "undefined" && typeof window.matchMedia === "function"
      ? window.matchMedia(query).matches
      : false;
  const [matches, setMatches] = useState<boolean>(read);
  useEffect(() => {
    if (typeof window === "undefined" || typeof window.matchMedia !== "function") return;
    const mql = window.matchMedia(query);
    // Re-read on mount: the viewport can change between the initialiser and here.
    setMatches(mql.matches);
    const onChange = (e: MediaQueryListEvent) => setMatches(e.matches);
    mql.addEventListener("change", onChange);
    return () => mql.removeEventListener("change", onChange);
  }, [query]);
  return matches;
}

const HeaderSky = () => {
  const { logout } = useMsalAuth();

  // Use extracted hooks
  // ⚠️ WIDTH, not user agent — these gates must agree with the CSS beside them.
  //
  // Both headers are shown/hidden purely by CSS at 992px (`hidden! md:flex!` and
  // `md:hidden!`, --breakpoint-md: 992px). Gating their CONTENTS on
  // useMobileDetection — a user-agent regex, constant per session — made the two
  // disagree: on a desktop UA under 992px the desktop header is display:none and
  // the mobile header rendered neither RecentlyViewed nor the bell, so both were
  // unreachable. Half-screen on a large display, DevTools device mode, Surface,
  // and iPad all land there (iPadOS reports a Macintosh UA). The mirror case is
  // an Android tablet at >=992px, where a mobile UA hides them from the desktop
  // header that IS showing.
  //
  // Same false premise that made the nav toggle inert, twelve lines below.
  // Keyed off the real gate, the two cannot drift.
  // ⚠️ The NEGATION of the CSS gate, not a max-width one pixel under it.
  //
  // `md:` is `min-width: 992px`, and `(max-width: 991px)` is NOT its complement:
  // a viewport strictly between them — routine under browser zoom or a
  // fractional DPR — satisfies neither. The CSS would show the mobile header
  // while `isNarrow` stayed false, so the bell and recently-viewed would render
  // in NO header at all. That is the same unreachable-controls bug this gate was
  // introduced to fix, narrowed to a sub-pixel band.
  //
  // Negating the identical query makes the two provably complementary, and
  // removes the hardcoded 991 that could drift from --breakpoint-md at all.
  // ⚠️ Seeded SYNCHRONOUSLY. The design system's useMediaQuery starts at `false`
  // and only reads matchMedia in an effect, so on a >=992px viewport the first
  // paint has isNarrow === true: the desktop toolbar renders with no tenant
  // logo, no RecentlyViewed and no NotificationBell, and they pop in one commit
  // later — a visible shift on every reload of AppLayout. The hook this replaced
  // computed its value in a useState initialiser, so the first render was right.
  // useMatchesQuery below keeps the media-query semantics this gate needs (see
  // the reasoning above) and restores the correct first frame.
  const isNarrow = !useMatchesQuery("(min-width: 992px)");
  const { selectedInstanceId } = useHeaderData();

  // Local UI state
  const [sidebarOpen, setSidebarOpen] = useState<boolean>(false);

  // ⚠️ DO NOT ADD A RESET KEYED ON `isMobileDevice`. One was added here to clear
  // `sidebarOpen` "when leaving mobile width" and it made navigation UNREACHABLE
  // below 992px on every desktop browser, because the premise was wrong twice
  // over:
  //
  //   · `useMobileDetection` is a USER-AGENT regex with [] deps — constant for
  //     the session and false on desktop. It measures no width at all (and does
  //     not list iPad).
  //   · this mobile header is gated on CSS (`md:hidden!`, --breakpoint-md 992px)
  //     and the drawer below is rendered with `isMobile` hardcoded true, so both
  //     appear on a desktop UA under 992px, where the flag says false.
  //
  // So the effect fired the instant the toggle set `sidebarOpen`, closing the
  // drawer on the same render pass. Half-screen on a 1920 display, DevTools
  // device mode without UA spoofing, iPad, Surface — the hamburger is the ONLY
  // route to navigation there, and it did nothing.
  //
  // No reset is needed: above 992px this whole block is display:none, so a stale
  // `sidebarOpen` is invisible, and coming back under 992px the icon correctly
  // reflects the drawer that is genuinely open. If a reset is ever truly wanted,
  // drive it off matchMedia("(min-width: 992px)") — the actual gate.

  const renderDesktopLogo = () => {
    if (isNarrow) return null;

    return (
      <SkyLink
        href={ROUTES.HOME + "?instanceid=" + selectedInstanceId}
        className="bg-surface-inverted h-[50px] border-r-0 p-2 min-w-[130%]"
      >
        <div className="flex justify-start items-center">
          <TenantLogo />
        </div>
      </SkyLink>
    );
  };

  return (
    <div
      className="min-w-full sticky z-[10001]"
      // Sticks BELOW the fixed app-top banner slot (HeaderBanner). `--app-banner-h`
      // is the live height of the active banner stack (0px when empty), so the
      // header never collides with the banner at any scroll position.
      style={{ top: "var(--app-banner-h, 0px)" }}
    >
      {/* Stale-bundle guard: offers a refresh when a newer build is deployed
          (renders nothing until then). */}
      <NewVersionBanner />
      {/* Desktop Header - visible at md (992px+) per Bootstrap breakpoints */}
      <div
        role="toolbar"
        aria-label="Header toolbar"
        className="hidden! md:flex! bg-surface-inverted h-[52px] justify-between px-4 py-2 z-10000 text-white shadow-md"
      >
        <div className="flex items-center">{renderDesktopLogo()}</div>
        <div className="flex items-center gap-3">
          <GlobalCommunitySelector instanceId={selectedInstanceId} />
          <ModeToggle />
          {!isNarrow && <RecentlyViewedMenu />}
          {!isNarrow && <NotificationBell />}
          <ProfileMenu onSignOut={logout} />
        </div>
      </div>

      {/* Mobile Header - visible below md (992px) per Bootstrap breakpoints */}
      <div className="md:hidden!">
        <div
          role="toolbar"
          aria-label="Mobile header toolbar"
          className="bg-surface-inverted h-14 flex items-center justify-between px-3 z-10000 text-white"
        >
          <div className="flex items-center gap-3">
            {/* THE ICON FOLLOWS THE DRAWER'S STATE, which is the whole point.
                The design system's own SkyNavDrawer toggle swaps between exactly
                these two glyphs — `Menu` while the nav is closed ("Expand
                navigation") and `PanelLeftClose` while it is open ("Collapse
                navigation") — and every other Skypoint product gets that for free
                because it lets the library render the toggle.

                Below `md` we do NOT: the library drawer is rendered without
                `collapsible`, so it draws no toggle and this button stands in for
                it. It was pinned to one glyph and to `setSidebarOpen(true)`, so
                with the drawer open it still showed a hamburger AND did nothing
                when pressed — the state the nav was in was unreadable, and there
                was no way back out of the drawer from here.

                (The glyph was also `Navigation` — lucide's paper-plane arrow,
                aliased to the FLUENT name for a hamburger during the migration.
                The name matched; the icon did not.) */}
            <SkyButton
              icon={sidebarOpen ? PanelLeftClose : Menu}
              appearance="transparent"
              className="text-white! min-w-10! h-10!"
              aria-label={sidebarOpen ? "Close menu" : "Open menu"}
              aria-expanded={sidebarOpen}
              // aria-expanded without aria-controls states a condition without
              // saying what is in it; a screen reader announces "expanded" and
              // the user has no way to know of what.
              // Conditional: sidebar.tsx returns null while closed, so a
              // permanent aria-controls would name an element that does not
              // exist in exactly the state the button is usually read in —
              // which axe flags and which tells a screen reader nothing.
              aria-controls={sidebarOpen ? MOBILE_DRAWER_ID : undefined}
              onClick={() => setSidebarOpen((open) => !open)}
            />
            <SkyLink
              href={ROUTES.HOME + "?instanceid=" + selectedInstanceId}
              className="flex items-center"
            >
              <TenantLogo className="h-[34px]" />
            </SkyLink>
          </div>
          <div className="flex items-center gap-2">
            <ModeToggle />
            {isNarrow && <RecentlyViewedMenu />}
            {isNarrow && <NotificationBell />}
            <ProfileMenu onSignOut={logout} />
          </div>
        </div>
        {/* Mobile Sidebar - SidebarSky handles backdrop and drawer internally */}
        <SidebarSky
          isMobile
          isOpen={sidebarOpen}
          onOpenChange={setSidebarOpen}
        />
      </div>
    </div>
  );
};

export default HeaderSky;
